import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import LoadingFallback from './components/common/LoadingFallback';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PaymentSuccessPage from './pages/auth/PaymentSuccessPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import APMPage from './pages/apm/APMPage';
import TracesPage from './pages/apm/TracesPage';
import TraceDetailsPage from './pages/apm/TraceDetailsPage';
import TransactionsPage from './pages/apm/TransactionsPage';
import APMErrorsPage from './pages/apm/APMErrorsPage';
import ServiceMapPage from './pages/apm/ServiceMapPage';
import AnomalyDetectionPage from './pages/apm/AnomalyDetectionPage';
import LogsPage from './pages/logs';
import LogsIntegrationPage from './pages/logs/LogsIntegrationPage';
import MetricsPage from './pages/metrics/MetricsPage';
import InfrastructurePage from './pages/infrastructure/InfrastructurePage';
import HostsPage from './pages/infrastructure/HostsPage';
import HostDetailsPage from './pages/infrastructure/HostDetailsPage';
import ContainersPage from './pages/infrastructure/ContainersPage';
import KubernetesPage from './pages/infrastructure/KubernetesPage';
import LogCollectionPage from './pages/infrastructure/LogCollectionPage';
import MLInsightsPage from './pages/ml/MLInsightsPage';
import AnomaliesPage from './pages/ml/AnomaliesPage';
import ForecastsPage from './pages/ml/ForecastsPage';
import RootCausePage from './pages/ml/RootCausePage';
import AlertsPage from './pages/alerts/AlertsPage';
import DatabasePage from './pages/database/DatabasePage';
import SettingsPage from './pages/settings/SettingsPage';
import UsersPage from './pages/settings/UsersPage';
import ApplicationsPage from './pages/settings/ApplicationsPage';
import BillingPage from './pages/settings/BillingPage';
import APIKeysPage from './pages/settings/APIKeysPage';
import WhiteLabelPage from './pages/settings/WhiteLabelPage';
import PlatformsPage from './pages/settings/PlatformsPage';
import MCPPage from './pages/settings/MCPPage';
import CompanyPage from './pages/settings/CompanyPage';
import StatusPagesPage from './pages/status-page/StatusPagesPage';
import StatusPageManagePage from './pages/status-page/StatusPageManagePage';
import PublicStatusPage from './pages/status-page/PublicStatusPage';
import AgentsPage from './pages/agents/AgentsPage';
import HostAgentInstallPage from './pages/agents/HostAgentInstallPage';
import KubernetesAgentInstallPage from './pages/agents/KubernetesAgentInstallPage';
import LogAgentInstallPage from './pages/agents/LogAgentInstallPage';
import APMAgentInstallPage from './pages/agents/APMAgentInstallPage';
import PlansPage from './pages/admin/PlansPage';
import TenantsPage from './pages/admin/TenantsPage';
import AdminUsersPage from './pages/admin/UsersPage';
import AffiliatesPage from './pages/admin/AffiliatesPage';
import SubscriptionsPage from './pages/admin/SubscriptionsPage';
import InvoicesPage from './pages/admin/InvoicesPage';
import AdminCloudScansPage from './pages/admin/AdminCloudScansPage';
import AffiliateDashboard from './pages/affiliate/AffiliateDashboard';
import CloudScanPage from './pages/cloud-scan/CloudScanPage';
import CloudScanDetailsPage from './pages/cloud-scan/CloudScanDetailsPage';
import ProtectedRoute from './components/common/ProtectedRoute';

// RUM Pages (moved to Analytics)
import RealUserMonitoringPage from './pages/analytics/RealUserMonitoringPage';
import SessionDetailsPage from './pages/rum/SessionDetailsPage';
import ErrorGroupDetailsPage from './pages/errors/ErrorGroupDetailsPage';

// Analytics Pages - Using lazy loading to avoid ERR_BLOCKED_BY_CLIENT issues
import AnalyticsOverviewPage from './pages/analytics/AnalyticsOverviewPage';
const EventsPage = lazy(() => import('./pages/analytics/EventsPage'));
import FunnelsPage from './pages/analytics/FunnelsPage';
import FunnelDetailsPage from './pages/analytics/FunnelDetailsPage';
import RetentionPage from './pages/analytics/RetentionPage';
import UserJourneysPage from './pages/analytics/UserJourneysPage';
import SDKSetupPage from './pages/analytics/SDKSetupPage';
import GeographyPage from './pages/analytics/GeographyPage';
import DevicesPage from './pages/analytics/DevicesPage';
import PagesPage from './pages/analytics/PagesPage';
import TrafficPage from './pages/analytics/TrafficPage';
import UserProfilePage from './pages/analytics/UserProfilePage';

// Mobile Analytics Pages
import MobileSDKSetupPage from './pages/analytics/mobile/MobileSDKSetupPage';
import MobileJourneysPage from './pages/analytics/mobile/MobileJourneysPage';
import MobileSessionDetailsPage from './pages/analytics/mobile/MobileSessionDetailsPage';

// Uptime Monitor Pages
import UptimeMonitorsPage from './pages/uptime/UptimeMonitorsPage';

// Security Pages
import SecurityDashboardPage from './pages/security/SecurityDashboardPage';
import SecurityScanDetailsPage from './pages/security/SecurityScanDetailsPage';

// SEO Pages
import SEOScannerPage from './pages/seo/SEOScannerPage';

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Document title is handled by WhiteLabelContext

    // Capture referral code from URL
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');

    if (refCode) {
      // Save referral code to localStorage
      localStorage.setItem('referral_code', refCode);
      console.log('Referral code saved:', refCode);

      // Redirect to register page with referral info
      navigate('/register?ref=' + refCode);
    }
  }, [location, navigate]);

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/payment-success" element={<PaymentSuccessPage />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* APM Routes */}
      <Route
        path="/apm"
        element={
          <ProtectedRoute>
            <APMPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apm/traces"
        element={
          <ProtectedRoute>
            <TracesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apm/traces/:applicationId/:traceId"
        element={
          <ProtectedRoute>
            <TraceDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apm/service-map"
        element={
          <ProtectedRoute>
            <ServiceMapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apm/transactions"
        element={
          <ProtectedRoute>
            <TransactionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apm/errors"
        element={
          <ProtectedRoute>
            <APMErrorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apm/anomalies"
        element={
          <ProtectedRoute>
            <AnomalyDetectionPage />
          </ProtectedRoute>
        }
      />

      {/* Logs Routes */}
      <Route
        path="/logs"
        element={
          <ProtectedRoute>
            <LogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs/integration"
        element={
          <ProtectedRoute>
            <LogsIntegrationPage />
          </ProtectedRoute>
        }
      />

      {/* Metrics Routes */}
      <Route
        path="/metrics"
        element={
          <ProtectedRoute>
            <MetricsPage />
          </ProtectedRoute>
        }
      />

      {/* Infrastructure Routes */}
      <Route
        path="/infrastructure"
        element={
          <ProtectedRoute>
            <InfrastructurePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/infrastructure/hosts"
        element={
          <ProtectedRoute>
            <HostsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/infrastructure/hosts/:id"
        element={
          <ProtectedRoute>
            <HostDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/infrastructure/containers"
        element={
          <ProtectedRoute>
            <ContainersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/infrastructure/kubernetes"
        element={
          <ProtectedRoute>
            <KubernetesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/infrastructure/log-collection"
        element={
          <ProtectedRoute>
            <LogCollectionPage />
          </ProtectedRoute>
        }
      />

      {/* ML/AI Routes */}
      <Route
        path="/ml"
        element={
          <ProtectedRoute>
            <MLInsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ml/anomalies"
        element={
          <ProtectedRoute>
            <AnomaliesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ml/forecasts"
        element={
          <ProtectedRoute>
            <ForecastsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ml/insights"
        element={
          <ProtectedRoute>
            <RootCausePage />
          </ProtectedRoute>
        }
      />

      {/* Alerts Routes */}
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />

      {/* Database Routes - under APM */}
      <Route
        path="/apm/database"
        element={
          <ProtectedRoute>
            <DatabasePage />
          </ProtectedRoute>
        }
      />

      {/* Status Page Routes - Public (no auth required) */}
      <Route path="/status/:slug" element={<PublicStatusPage />} />

      {/* Status Page Routes - Admin (protected) */}
      <Route
        path="/status-pages/:id"
        element={
          <ProtectedRoute>
            <StatusPageManagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/status-pages"
        element={
          <ProtectedRoute>
            <StatusPagesPage />
          </ProtectedRoute>
        }
      />

      {/* Settings Routes */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/applications"
        element={
          <ProtectedRoute>
            <ApplicationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/api-keys"
        element={
          <ProtectedRoute>
            <APIKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/white-label"
        element={
          <ProtectedRoute>
            <WhiteLabelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/platforms"
        element={
          <ProtectedRoute>
            <PlatformsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/mcp"
        element={
          <ProtectedRoute>
            <MCPPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/company"
        element={
          <ProtectedRoute>
            <CompanyPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/plans"
        element={
          <ProtectedRoute>
            <PlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute>
            <TenantsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/affiliates"
        element={
          <ProtectedRoute>
            <AffiliatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute>
            <SubscriptionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cloud-scans"
        element={
          <ProtectedRoute>
            <AdminCloudScansPage />
          </ProtectedRoute>
        }
      />

      {/* Affiliate Routes */}
      <Route
        path="/affiliate/dashboard"
        element={
          <ProtectedRoute>
            <AffiliateDashboard />
          </ProtectedRoute>
        }
      />

      {/* Agents Routes */}
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <AgentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/host/install"
        element={
          <ProtectedRoute>
            <HostAgentInstallPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/kubernetes/install"
        element={
          <ProtectedRoute>
            <KubernetesAgentInstallPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/logs/install"
        element={
          <ProtectedRoute>
            <LogAgentInstallPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/apm/install"
        element={
          <ProtectedRoute>
            <APMAgentInstallPage />
          </ProtectedRoute>
        }
      />

      {/* Cloud Scanner Routes */}
      <Route
        path="/cloud-scan/:id"
        element={
          <ProtectedRoute>
            <CloudScanDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cloud-scan"
        element={
          <ProtectedRoute>
            <CloudScanPage />
          </ProtectedRoute>
        }
      />

      {/* Uptime Monitor Routes */}
      <Route
        path="/uptime"
        element={
          <ProtectedRoute>
            <UptimeMonitorsPage />
          </ProtectedRoute>
        }
      />

      {/* Security Routes */}
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <SecurityDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security/:id"
        element={
          <ProtectedRoute>
            <SecurityScanDetailsPage />
          </ProtectedRoute>
        }
      />

      {/* SEO Scanner */}
      <Route
        path="/seo-scanner"
        element={
          <ProtectedRoute>
            <SEOScannerPage />
          </ProtectedRoute>
        }
      />

      {/* RUM Routes (moved to Analytics) */}
      <Route
        path="/analytics/rum"
        element={
          <ProtectedRoute>
            <RealUserMonitoringPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/rum/sessions/:id"
        element={
          <ProtectedRoute>
            <SessionDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/rum/errors/:id"
        element={
          <ProtectedRoute>
            <ErrorGroupDetailsPage />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/rum/*" element={<Navigate to="/analytics/rum" replace />} />
      <Route path="/errors/*" element={<Navigate to="/analytics/rum" replace />} />

      {/* Analytics Routes */}
      <Route
        path="/analytics/overview"
        element={
          <ProtectedRoute>
            <AnalyticsOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/events"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <EventsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/funnels"
        element={
          <ProtectedRoute>
            <FunnelsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/funnels/:id"
        element={
          <ProtectedRoute>
            <FunnelDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/retention"
        element={
          <ProtectedRoute>
            <RetentionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/journeys"
        element={
          <ProtectedRoute>
            <UserJourneysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/geography"
        element={
          <ProtectedRoute>
            <GeographyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/devices"
        element={
          <ProtectedRoute>
            <DevicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/pages"
        element={
          <ProtectedRoute>
            <PagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/traffic"
        element={
          <ProtectedRoute>
            <TrafficPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/users/:userId"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/sdk-setup"
        element={
          <ProtectedRoute>
            <SDKSetupPage />
          </ProtectedRoute>
        }
      />

      {/* Mobile SDK Routes */}
      <Route
        path="/analytics/mobile/setup"
        element={
          <ProtectedRoute>
            <MobileSDKSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/mobile/journeys"
        element={
          <ProtectedRoute>
            <MobileJourneysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/mobile/session/:id"
        element={
          <ProtectedRoute>
            <MobileSessionDetailsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsOverviewPage />
          </ProtectedRoute>
        }
      />

      {/* Public pages */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      {/* Publica: endereco exigido no cadastro de apps e por integracoes. */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/privacidade" element={<PrivacyPage />} />

      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
}

export default App;
